import CloseIcon from "@mui/icons-material/Close";
import AutoGraphRoundedIcon from "@mui/icons-material/AutoGraphRounded";
import CampaignRoundedIcon from "@mui/icons-material/CampaignRounded";
import SpeedRoundedIcon from "@mui/icons-material/SpeedRounded";
import { Box, Dialog, IconButton, Stack, Typography } from "@mui/material";
import AcxButton from "components/UI/AcxButton";
import { observer } from "mobx-react";
import { useStore } from "utils/useStore";
import { AgentLogoWithNameFull } from "./Branding/AgentLogoWithNameFull";
import { PaywallRightPaneImaging } from "./Branding/PaywallRightPaneImaging";
import AgentStore from "./Stores/AgentStore";

const featureItems = [
    {
        title: "Spot What's Changing",
        description:
            "Identify moments that signal risk or opportunity without manual analysis.",
        icon: AutoGraphRoundedIcon,
    },
    {
        title: "Get Actionable Recommendations",
        description:
            "KBo will sift through all available tools to provide you with tailored insights that let you prioritize clear next steps with confidence",
        icon: CampaignRoundedIcon,
    },
    {
        title: "Drive Faster Impact",
        description:
            "Spend less time uncovering or investigating issues, and more time improving outcomes for your customers",
        icon: SpeedRoundedIcon,
    },
];

export const KboPaywallDialog = observer(() => {
    const agentStore = useStore(AgentStore);

    return (
        <Dialog
            open={agentStore.isPaywallDialogOpen}
            onClose={() => agentStore.closePaywallDialog()}
            maxWidth={false}
            PaperProps={{
                sx: {
                    borderRadius: 2,
                    overflow: "hidden",
                    width: "min(1040px, calc(100vw - 32px))",
                    maxWidth: "1040px",
                },
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "row",
                    minHeight: {
                        md: 640,
                    },
                    position: "relative",
                }}
            >
                <IconButton
                    aria-label="Close KBo paywall"
                    onClick={() => agentStore.closePaywallDialog()}
                    sx={{
                        position: "absolute",
                        right: 16,
                        top: 16,
                        zIndex: 1,
                        color: "gray.400",
                    }}
                >
                    <CloseIcon />
                </IconButton>
                <Stack
                    spacing={4}
                    sx={{
                        flex: {
                            xs: "1 1 auto",
                            md: "0 0 50%",
                        },
                        paddingX: {
                            xs: 3,
                            md: 6,
                        },
                        paddingY: {
                            xs: 5,
                            md: 6,
                        },
                        justifyContent: "center",
                    }}
                >
                    <Stack spacing={1.5}>
                        <Typography
                            sx={{
                                color: "gray.900",
                                fontSize: "24px",
                                fontWeight: 700,
                                lineHeight: "32px",
                            }}
                        >
                            Unlock insights with{" "}
                            <Box
                                component="span"
                                sx={{
                                    display: "inline-flex",
                                    verticalAlign: "middle",
                                    transform: "translateY(-1px)",
                                }}
                            >
                                <AgentLogoWithNameFull
                                    width="71"
                                    height="24"
                                />
                            </Box>
                        </Typography>
                        <Typography
                            sx={{
                                color: "gray.500",
                                fontSize: "16px",
                                lineHeight: "24px",
                            }}
                        >
                            Your AI assistant for turning conversations into
                            action.
                        </Typography>
                    </Stack>
                    <Stack spacing={3}>
                        {featureItems.map((item) => {
                            const Icon = item.icon;

                            return (
                                <Stack
                                    key={item.title}
                                    direction="row"
                                    spacing={2}
                                    alignItems="flex-start"
                                >
                                    <Icon
                                        sx={{
                                            color: "#274472",
                                            fontSize: 28,
                                            marginTop: "2px",
                                        }}
                                    />
                                    <Stack spacing={0.5}>
                                        <Typography
                                            sx={{
                                                color: "gray.700",
                                                fontSize: "18px",
                                                fontWeight: 700,
                                                lineHeight: "28px",
                                            }}
                                        >
                                            {item.title}
                                        </Typography>
                                        <Typography
                                            sx={{
                                                color: "gray.500",
                                                fontSize: "16px",
                                                lineHeight: "24px",
                                                maxWidth: 460,
                                            }}
                                        >
                                            {item.description}
                                        </Typography>
                                    </Stack>
                                </Stack>
                            );
                        })}
                    </Stack>
                    <Stack spacing={2} sx={{ paddingTop: 1 }}>
                        <AcxButton
                            fullWidth
                            loading={agentStore.getTaskLoading(
                                AgentStore.Tasks.RequestAccess,
                            )}
                            disabled={agentStore.isAccessRequested}
                            onClick={() => agentStore.requestAccess()}
                            sx={{
                                minHeight: 40,
                                fontWeight: 700,
                            }}
                        >
                            {agentStore.isAccessRequested
                                ? "We'll be in touch shortly!"
                                : "Request Access to KBo"}
                        </AcxButton>
                        <Typography
                            component="a"
                            href="https://authenticx.com/resources/meet-kbo-your-ai-assistant-and-conversation-guide/"
                            target="_blank"
                            rel="noreferrer"
                            align="center"
                            sx={{
                                color: "gray.400",
                                fontSize: "14px",
                                fontWeight: 500,
                                lineHeight: "20px",
                                textDecoration: "underline",
                                textUnderlineOffset: "3px",
                            }}
                        >
                            Learn more about KBo
                        </Typography>
                    </Stack>
                </Stack>
                <Box
                    sx={{
                        display: {
                            xs: "none",
                            md: "flex",
                        },
                        flex: {
                            md: "0 0 50%",
                        },
                        alignItems: "stretch",
                        justifyContent: "center",
                        background:
                            "linear-gradient(90deg, #1D2A60 0%, #239C98 100%)",
                        overflow: "hidden",
                    }}
                >
                    <Box
                        sx={{
                            flex: 1,
                            display: "flex",
                            alignItems: "stretch",
                        }}
                    >
                        <Box
                            sx={{
                                position: "relative",
                                flex: 1,
                                overflow: "hidden",
                                minHeight: {
                                    xs: 360,
                                    md: "100%",
                                },
                            }}
                        >
                            <Box
                                sx={{
                                    position: "absolute",
                                    inset: 0,
                                    display: "flex",
                                    alignItems: "flex-end",
                                    justifyContent: "center",
                                    overflow: "hidden",
                                    "& svg": {
                                        display: "block",
                                        height: {
                                            xs: "auto",
                                            md: "calc(100% + 18px)",
                                        },
                                        width: {
                                            xs: "100%",
                                            md: "auto",
                                        },
                                        maxWidth: "100%",
                                        maxHeight: {
                                            xs: "100%",
                                            md: "none",
                                        },
                                        transform: {
                                            xs: "none",
                                            md: "translateY(18px)",
                                        },
                                    },
                                }}
                            >
                                <PaywallRightPaneImaging />
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Dialog>
    );
});
